import React, { useCallback } from 'react';

import styled from 'styled-components';

import { ISanityImage } from '@rbi-ctg/menu';
import { ClickableContainer } from 'components/clickable-container';
import SwapIcon from 'components/icons/swap';
import Picture from 'components/picture';

import { TASTY_SWAP_CARD_WIDTH_PX_DESKTOP } from './constants';
import { ISwap } from './types';

const Container = styled(ClickableContainer)`
  border-radius: 8px;
  width: 100%;
  max-width: ${Styles.layout.smallerSectionMaxWidth};
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  background-color: ${Styles.color.white};
  box-shadow: ${Styles.boxShadow};

  &:hover {
    box-shadow: ${Styles.boxShadow}, ${Styles.boxShadow}, ${Styles.boxShadow};
  }

  &:active {
    opacity: 0.7;
  }
`;

const ImagesContainer = styled.div`
  position: relative;
  width: 100%;
  height: 0px;
  /* this maintains a 9/20 aspect ratio */
  padding-bottom: 45%;
  overflow: hidden;
`;

const ProductImagesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ProductImage = styled(Picture)`
  height: 100%;
  flex: 1 1 auto;
`;

const BackgroundImage = styled(Picture)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

/* at max width, the container is TASTY_SWAP_CARD_WIDTH_PX_DESKTOP and the swap icon is 66px */
/* 66/TASTY_SWAP_CARD_WIDTH_PX_DESKTOP = x% */
const swapIconContainerWidthPercentage = (66 / TASTY_SWAP_CARD_WIDTH_PX_DESKTOP) * 100;
/* the viewbox of the icon is 66 * 50 */
/* 50/66 = 75.75% */
/* x% (the width) of 75.75% is y% */
/* In short, this math maintains the aspect ratio of the swap icon
 * as a function of of the outer containers width */
const swapIconContainerHeightPercentage = swapIconContainerWidthPercentage * 0.7575 * 100;

const SwapIconContainer = styled.div`
  position: absolute;
  top: 30%;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  width: ${swapIconContainerWidthPercentage}%;
  height: 0;
  padding-bottom: ${swapIconContainerHeightPercentage}%;

  svg {
    top: 0;
    left: 0;
  }
`;

const TextContainer = styled.div`
  width: 100%;
  padding: 0.8rem 1.25rem 0.9rem;

  text-overflow: ellipsis;
  overflow: hidden;

  ${Styles.desktop`
     padding: 1rem 1.8rem 2rem;
  `};
`;

const ProductName = styled.h4`
  margin: 0;
  text-align: left;
  font-size: 1.2rem;
  text-transform: ${Styles.textTransform.headlines};
  font-family: ${Styles.fontFamily.base};
  color: ${Styles.color.black};

  ${Styles.desktop`
    font-size: 1.4rem;
  `};
`;

const SwapText = styled.p`
  width: 100%;
  margin: 0;
  text-align: left;
  font-size: 0.7rem;
  text-transform: ${Styles.textTransform.body};
  font-family: ${Styles.fontFamily.body};
  color: ${Styles.color.grey.one};

  /* keep the text at 2 lines*/
  line-height: 1.1rem;
  height: 2.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  ${Styles.desktop`
    font-size: 0.8rem;
    margin-top: 0.2rem;
  `};
`;

interface ITastySwapCard {
  swap: ISwap;
  backgroundImage?: ISanityImage;
  onClick: (swap: ISwap) => void;
}

export const TastySwapCard: React.FC<ITastySwapCard> = ({ backgroundImage, onClick, swap }) => {
  const { selectedProduct, suggestedProduct, swapTitle } = swap;
  const onSelectTastySwap = useCallback(() => {
    onClick(swap);
  }, [onClick, swap]);

  return (
    <Container onClick={onSelectTastySwap}>
      <ImagesContainer>
        <ProductImagesContainer>
          {backgroundImage && <BackgroundImage alt="" image={backgroundImage} />}
          <ProductImage alt={selectedProduct.name} objectFitContain image={selectedProduct.image} />
          <ProductImage
            alt={suggestedProduct.name}
            objectFitContain
            image={suggestedProduct.image}
          />
          <SwapIconContainer>
            <SwapIcon />
          </SwapIconContainer>
        </ProductImagesContainer>
      </ImagesContainer>
      <TextContainer>
        <ProductName>{selectedProduct.name}</ProductName>
        <SwapText>{swapTitle}</SwapText>
      </TextContainer>
    </Container>
  );
};
